import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-policy-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Legal</span>
            <h1 className="font-serif text-5xl md:text-6xl text-gray-900 mt-4 mb-6">
              Privacy Policy
            </h1>
            <p className="text-gray-500">Last updated: January 2025</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-6">
              Drop24 ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you visit our 
              website or use our delivery services.
            </p>
            <p className="text-gray-600 mb-6">
              By using our services, you agree to the collection and use of information in accordance 
              with this policy. If you do not agree with the terms of this privacy policy, please do 
              not access the site or use our services.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
            <p className="text-gray-600 mb-4">We may collect information about you in a variety of ways:</p>
            
            <h3 className="font-semibold text-lg text-gray-900 mt-6 mb-3">Personal Data</h3>
            <p className="text-gray-600 mb-4">
              When you contact us or use our services, we may collect personally identifiable information, such as:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Name and contact information (email address, phone number)</li>
              <li>Business name and address</li>
              <li>Delivery addresses and recipient information</li>
              <li>Order and shipment details</li>
              <li>Payment information (processed securely through third-party providers)</li>
            </ul>

            <h3 className="font-semibold text-lg text-gray-900 mt-6 mb-3">Usage Data</h3>
            <p className="text-gray-600 mb-6">
              We automatically collect certain information when you visit our website, including your 
              IP address, browser type, operating system, access times, and the pages you have viewed. 
              This information is used to improve our website and services.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Provide, operate, and maintain our delivery services</li>
              <li>Process and complete transactions</li>
              <li>Send you delivery updates and tracking information</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send you marketing and promotional communications (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">4. Sharing Your Information</h2>
            <p className="text-gray-600 mb-4">We may share your information in the following situations:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li><strong>Service Providers:</strong> We may share your information with third-party service providers who perform services on our behalf, such as payment processing and delivery tracking.</li>
              <li><strong>Business Partners:</strong> We may share information with our business partners to offer certain products, services, or promotions.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information where required by law or to protect our rights.</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition.</li>
            </ul>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">5. Cookies and Tracking Technologies</h2>
            <p className="text-gray-600 mb-6">
              We use cookies and similar tracking technologies to track activity on our website and 
              hold certain information. Cookies are files with a small amount of data that are sent 
              to your browser from a website and stored on your device. You can instruct your browser 
              to refuse all cookies or to indicate when a cookie is being sent.
            </p>
            <p className="text-gray-600 mb-6">
              We use Google Tag Manager and analytics tools to understand how visitors interact with 
              our website. This helps us improve our services and user experience.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">6. Data Security</h2>
            <p className="text-gray-600 mb-6">
              We use administrative, technical, and physical security measures to protect your personal 
              information. While we have taken reasonable steps to secure the information you provide, 
              please be aware that no security measures are perfect or impenetrable, and no method of 
              data transmission can be guaranteed against interception or misuse.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">7. Data Retention</h2>
            <p className="text-gray-600 mb-6">
              We will retain your personal information only for as long as necessary to fulfil the 
              purposes for which it was collected, including to satisfy any legal, accounting, or 
              reporting requirements. Delivery and transaction records may be retained for up to 
              7 years for legal and business purposes.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">8. Your Rights</h2>
            <p className="text-gray-600 mb-4">
              Under the Protection of Personal Information Act (POPIA) of South Africa, you have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate or incomplete information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to the processing of your personal information</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with the Information Regulator</li>
            </ul>
            <p className="text-gray-600 mb-6">
              To exercise any of these rights, please contact us using the details provided below.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">9. Third-Party Links</h2>
            <p className="text-gray-600 mb-6">
              Our website may contain links to third-party websites. We are not responsible for the 
              privacy practices or content of these websites. We encourage you to read the privacy 
              policy of every website you visit.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-600 mb-6">
              Our services are not intended for individuals under the age of 18. We do not knowingly 
              collect personal information from children. If you become aware that a child has provided 
              us with personal information, please contact us immediately.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update our Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. You are 
              advised to review this Privacy Policy periodically for any changes.
            </p>

            <h2 className="font-serif text-2xl text-gray-900 mt-8 mb-4">12. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-2xl p-6 mb-8">
              <p className="text-gray-700 mb-2"><strong>Drop24 (Pty) Ltd</strong></p>
              <p className="text-gray-600 mb-1">Email: online@drop24.co.za</p>
              <p className="text-gray-600 mb-1">Phone: 066 071 9068</p>
              <p className="text-gray-600">Location: Gauteng, South Africa</p>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-drop24-red font-semibold hover:underline"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPolicyPage;
